import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import LayoutUser from '../components/layoutUser';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import Swal from 'sweetalert2';
import {
    FaUserCircle, FaCamera, FaSave, FaDownload, FaCheckCircle
} from 'react-icons/fa';

const ProfileUser = () => {
    const navigate = useNavigate();
    const [profileData, setProfileData] = useState(null);
    const [formData, setFormData] = useState({
        nis: '',
        nama_lengkap: '',
        nama_pengguna: '',
        kelas: '',
        alamat: '',
        kata_sandi: '',
    });
    const [loading, setLoading] = useState(true);

    const [selectedFile, setSelectedFile] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

    const fetchProfile = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            const decoded = jwtDecode(token);
            const userId = decoded.id;

            const response = await axios.get(`${process.env.REACT_APP_API_URL}/anggota/${userId}`, {
                headers: { 'x-auth-token': token },
            });

            const data = response.data;
            setProfileData(data);
            setFormData({
                nis: data.nis,
                nama_lengkap: data.nama_lengkap,
                nama_pengguna: data.nama_pengguna,
                kelas: data.kelas,
                alamat: data.alamat,
                kata_sandi: '',
            });
            setLoading(false);
        } catch (error) {
            Swal.fire('Error', 'Gagal memuat data profil.', 'error');
            setLoading(false);
            console.error(error);
        }
    }, [navigate]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                Swal.fire('Peringatan', 'Ukuran file maksimal 2MB!', 'warning');
                return;
            }
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                Swal.fire('Peringatan', 'Hanya file gambar (JPEG, PNG, GIF) yang diizinkan!', 'warning');
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleUploadPhoto = async () => {
        if (!selectedFile) {
            Swal.fire('Peringatan', 'Pilih file terlebih dahulu!', 'warning');
            return;
        }

        const token = localStorage.getItem('token');
        const decoded = jwtDecode(token);
        const userId = decoded.id;

        const photoFormData = new FormData();
        photoFormData.append('profile_photo', selectedFile);

        setIsUploadingPhoto(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/anggota/profile/upload-photo/${userId}`, photoFormData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'x-auth-token': token,
                },
            });

            Swal.fire('Berhasil!', response.data.message, 'success').then(() => {
                fetchProfile();
                setSelectedFile(null);
                const fileInput = document.querySelector('input[type="file"]');
                if (fileInput) fileInput.value = '';
            });
        } catch (error) {
            console.error('Gagal mengunggah foto:', error);
            Swal.fire('Error', error.response?.data?.message || 'Gagal mengunggah foto profil.', 'error');
        } finally {
            setIsUploadingPhoto(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        const decoded = jwtDecode(token);
        const userId = decoded.id;

        const updatedData = { ...formData };
        if (updatedData.kata_sandi === '') {
            delete updatedData.kata_sandi;
        }

        setIsUpdating(true);
        try {
            await axios.put(`${process.env.REACT_APP_API_URL}/anggota/${userId}`, updatedData, {
                headers: { 'x-auth-token': token },
            });

            Swal.fire('Berhasil!', 'Profil berhasil diperbarui.', 'success');
            fetchProfile();
        } catch (error) {
            Swal.fire('Gagal', error.response?.data?.message || 'Gagal memperbarui profil.', 'error');
            console.error(error);
        } finally {
            setIsUpdating(false);
        }
    };

    // 1. Tambahkan state baru di bagian atas component (setelah state yang sudah ada):
    const [bebasPustakaStatus, setBebasPustakaStatus] = useState({
        eligible: false,
        peminjaman_aktif: 0,
        denda_belum_bayar: 0,
        total_denda: 0,
        loading: true
    });

    // 2. Tambahkan fungsi untuk fetch status bebas pustaka:
    const fetchBebasPustakaStatus = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) return;

        try {
            const decoded = jwtDecode(token);
            const userId = decoded.id;

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/anggota/${userId}/bebas-pustaka/check`,
                { headers: { 'x-auth-token': token } }
            );

            setBebasPustakaStatus({
                ...response.data,
                loading: false
            });
        } catch (error) {
            console.error('Error fetching bebas pustaka status:', error);
            setBebasPustakaStatus(prev => ({ ...prev, loading: false }));
        }
    }, []);



    useEffect(() => {
        fetchProfile();
        fetchBebasPustakaStatus();
    }, [fetchProfile, fetchBebasPustakaStatus]);

    // 4. Tambahkan fungsi untuk download PDF:
    const handleDownloadBebasPustaka = async () => {
        const token = localStorage.getItem('token');
        const decoded = jwtDecode(token);
        const userId = decoded.id;

        try {
            Swal.fire({
                title: 'Generating PDF...',
                text: 'Mohon tunggu sebentar',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/anggota/${userId}/bebas-pustaka/generate`,
                {
                    headers: { 'x-auth-token': token },
                    responseType: 'blob'
                }
            );

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Bebas_Pustaka_${profileData.nis}_${profileData.nama_lengkap}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            Swal.fire({
                icon: 'success',
                title: 'Berhasil!',
                text: 'Surat Bebas Pustaka berhasil diunduh'
            });
        } catch (error) {
            console.error('Error downloading bebas pustaka:', error);
            Swal.fire({
                icon: 'error',
                title: 'Gagal!',
                text: error.response?.data?.message || 'Gagal mengunduh surat bebas pustaka'
            });
        }
    };

    if (loading) {
        return (
            <LayoutUser>
                <div className="section">
                    <div className="container">
                        <div className="has-text-centered">
                            <progress className="progress is-primary" max="100"></progress>
                            <p className="title is-5 mt-4">Memuat profil...</p>
                        </div>
                    </div>
                </div>
            </LayoutUser>
        );
    }

    if (!profileData) {
        return (
            <LayoutUser>
                <div className="section">
                    <div className="container">
                        <div className="notification is-warning">
                            <p>Data profil tidak ditemukan.</p>
                        </div>
                    </div>
                </div>
            </LayoutUser>
        );
    }

    const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

    const fotoProfileUrl = selectedFile
        ? URL.createObjectURL(selectedFile)  // Preview file baru
        : (profileData.foto_profile
            ? `${BASE_URL}${profileData.foto_profile}`  // Foto existing
            : null);

    return (
        <LayoutUser>
            {/* --- PENAMBAHAN ANIMASI DI SINI --- */}
            <style>{`
                @keyframes slideInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animated-component {
                    opacity: 0;
                    animation: slideInUp 0.6s ease-out forwards;
                }
            `}</style>

            <div className="container">
                {/* Header */}
                <div className="mb-4">
                    <h1 className="title is-3 has-text-dark">Profil Saya</h1>
                </div>

                <div className="columns is-variable is-8">
                    {/* Left Column - Edit Form */}
                    <div className="column is-7-desktop is-12-mobile animated-component">
                        <div className="box" style={{
                            border: '1px solid #959697ff',
                            borderRadius: '8px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                            <div className="has-text-centered mb-4">
                                <h3 className="title is-5 has-text-info">Edit Profil Saya</h3>
                            </div>

                            <form onSubmit={handleUpdate}>
                                <div className="field">
                                    <label className="label">
                                        Kode Anggota <span className="has-text-danger">*tidak dapat dirubah</span>
                                    </label>
                                    <div className="control">
                                        <input
                                            className="input"
                                            type="text"
                                            value={profileData.kode_anggota}
                                            disabled
                                            style={{ backgroundColor: '#f5f5f5' }}
                                        />
                                    </div>
                                </div>

                                <div className="field">
                                    <label className="label">
                                        NIS
                                    </label>
                                    <div className="control">
                                        <input
                                            className="input"
                                            type="text"
                                            name="nis"
                                            value={formData.nis}
                                            onChange={handleChange}
                                            required
                                            placeholder="Masukkan NIS"
                                        />
                                    </div>
                                </div>

                                <div className="field">
                                    <label className="label">
                                        Nama Lengkap
                                    </label>
                                    <div className="control">
                                        <input
                                            className="input"
                                            type="text"
                                            name="nama_lengkap"
                                            value={formData.nama_lengkap}
                                            onChange={handleChange}
                                            required
                                            placeholder="Masukkan nama lengkap"
                                        />
                                    </div>
                                </div>

                                <div className="field">
                                    <label className="label">
                                        Nama Pengguna
                                    </label>
                                    <div className="control">
                                        <input
                                            className="input"
                                            type="text"
                                            name="nama_pengguna"
                                            value={formData.nama_pengguna}
                                            onChange={handleChange}
                                            required
                                            placeholder="Masukkan nama pengguna"
                                        />
                                    </div>
                                </div>

                                <div className="field">
                                    <label className="label">
                                        Kata Sandi
                                    </label>
                                    <div className="control">
                                        <input
                                            className="input"
                                            type="password"
                                            name="kata_sandi"
                                            value={formData.kata_sandi}
                                            onChange={handleChange}
                                            placeholder="Kosongkan jika tidak ingin mengubah"
                                        />
                                    </div>
                                </div>

                                <div className="field">
                                    <label className="label">
                                        Kelas
                                    </label>
                                    <div className="control">
                                        <input
                                            className="input"
                                            type="text"
                                            name="kelas"
                                            value={formData.kelas}
                                            onChange={handleChange}
                                            required
                                            placeholder="Masukkan kelas"
                                        />
                                    </div>
                                </div>

                                <div className="field">
                                    <label className="label">
                                        Alamat Lengkap
                                    </label>
                                    <div className="control">
                                        <textarea
                                            className="textarea"
                                            name="alamat"
                                            value={formData.alamat}
                                            onChange={handleChange}
                                            required
                                            placeholder="Masukkan alamat lengkap"
                                            rows="3"
                                            style={{ resize: 'vertical' }}
                                        />
                                    </div>
                                </div>

                                <div className="field">
                                    <div className="control">
                                        <button
                                            className={`button is-info is-fullwidth ${isUpdating ? 'is-loading' : ''}`}
                                            type="submit"
                                            disabled={isUpdating}
                                        >
                                            Update
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Right Column - Profile Info & Status */}
                    <div className="column is-5-desktop is-12-mobile animated-component" style={{ animationDelay: '0.1s' }}>
                        {/* Profile Info Box */}
                        <div className="box mb-4" style={{
                            border: '1px solid #959697ff',
                            borderRadius: '8px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                            <div className="has-text-centered mb-4">
                                <h3 className="title is-5 has-text-info">Profil Saya</h3>
                            </div>

                            {/* Profile Photo */}
                            <div className="has-text-centered mb-4">
                                <figure className="image is-96x96 is-inline-block" style={{
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    border: '3px solid #959697ff'
                                }}>
                                    {fotoProfileUrl ? (
                                        <img
                                            src={fotoProfileUrl}
                                            alt="Foto Profil"
                                            style={{
                                                objectFit: 'cover',
                                                width: '100%',
                                                height: '100%'
                                            }}
                                        />
                                    ) : (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                            backgroundColor: '#f5f5f5'
                                        }}>
                                            <FaUserCircle size={60} className="has-text-grey-light" />
                                        </div>
                                    )}
                                </figure>

                                {/* Photo Upload Section */}
                                <div className="mt-3">
                                    <div className="file has-name is-small is-fullwidth mb-2">
                                        <label className="file-label">
                                            <input
                                                className="file-input"
                                                type="file"
                                                name="profile_photo"
                                                onChange={handleFileChange}
                                                accept="image/png,image/jpeg,image/jpg,image/gif"
                                            />
                                            <span className="file-cta">
                                                <span className="file-icon">
                                                    <FaCamera />
                                                </span>
                                                <span className="file-label">Pilih Foto</span>
                                            </span>
                                            {selectedFile && (
                                                <span className="file-name" style={{ fontSize: '0.8rem' }}>
                                                    {selectedFile.name.length > 15
                                                        ? selectedFile.name.substring(0, 15) + '...'
                                                        : selectedFile.name}
                                                </span>
                                            )}
                                        </label>
                                    </div>

                                    {selectedFile && (
                                        <button
                                            className={`button is-success is-small is-fullwidth ${isUploadingPhoto ? 'is-loading' : ''}`}
                                            onClick={handleUploadPhoto}
                                            disabled={isUploadingPhoto}
                                        >
                                            <span className="icon is-small">
                                                <FaSave />
                                            </span>
                                            <span>Upload Foto</span>
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Profile Details */}
                            <div className="content">
                                <div className="field">
                                    <strong>Kode Anggota:</strong> {profileData.kode_anggota}
                                </div>
                                <div className="field">
                                    <strong>NIS:</strong> {profileData.nis}
                                </div>
                                <div className="field">
                                    <strong>Nama Lengkap:</strong> {profileData.nama_lengkap}
                                </div>
                                <div className="field">
                                    <strong>Nama Pengguna:</strong> {profileData.nama_pengguna}
                                </div>
                                <div className="field">
                                    <strong>Kata Sandi:</strong> {profileData.kelas}
                                </div>
                                <div className="field">
                                    <strong>Kelas:</strong> {profileData.kelas}
                                </div>
                                <div className="field">
                                    <strong>Alamat Lengkap:</strong> {profileData.alamat}
                                </div>
                            </div>
                        </div>

                        {/* Bebas Pustaka Status */}
                        <div className="box" style={{
                            border: '1px solid #959697ff',
                            borderRadius: '8px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            backgroundColor: bebasPustakaStatus.eligible ? '#f8fff9' : '#fffbf5'
                        }}>
                            <div className="has-text-centered mb-4">
                                <h3 className={`title is-5 ${bebasPustakaStatus.eligible ? 'has-text-success' : 'has-text-warning'}`}>
                                    Bebas Pustaka
                                </h3>
                            </div>

                            {bebasPustakaStatus.loading ? (
                                <div className="has-text-centered">
                                    <progress className="progress is-small is-primary" max="100"></progress>
                                    <p className="is-size-7">Memuat status...</p>
                                </div>
                            ) : (
                                <div className="content">
                                    <div className="field mb-3">
                                        <div className="is-flex is-align-items-center is-justify-content-space-between">
                                            <div className="is-flex is-align-items-center">
                                                <span className="mr-2">1.</span>
                                                <span className="mr-2">Pengembalian Buku</span>
                                            </div>
                                            {bebasPustakaStatus.peminjaman_aktif === 0 ? (
                                                <FaCheckCircle className="has-text-success" />
                                            ) : (
                                                <span className="tag is-warning is-light">
                                                    {bebasPustakaStatus.peminjaman_aktif} aktif
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="field mb-4">
                                        <div className="is-flex is-align-items-center is-justify-content-space-between">
                                            <div className="is-flex is-align-items-center">
                                                <span className="mr-2">2.</span>
                                                <span className="mr-2">Denda</span>
                                            </div>
                                            {bebasPustakaStatus.total_denda === 0 ? (
                                                <FaCheckCircle className="has-text-success" />
                                            ) : (
                                                <span className="tag is-danger is-light">
                                                    Rp {bebasPustakaStatus.total_denda.toLocaleString('id-ID')}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {!bebasPustakaStatus.eligible && (
                                        <div className="notification is-warning is-light mb-3">
                                            <p className="is-size-7">
                                                Selesaikan peminjaman dan bayar denda terlebih dahulu untuk dapat mengunduh Bebas Pustaka.
                                            </p>
                                        </div>
                                    )}

                                    <button
                                        className={`button is-fullwidth ${bebasPustakaStatus.eligible ? 'is-info' : 'is-light'}`}
                                        onClick={handleDownloadBebasPustaka}
                                        disabled={!bebasPustakaStatus.eligible}
                                    >
                                        <span className="icon">
                                            <FaDownload />
                                        </span>
                                        <span>Download Bebas Pustaka</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </LayoutUser>
    );
};

export default ProfileUser;